/**
 * Quick add on Design cards (owner, 31 Oct 2026: "an add to cart in the card as a quick action"). A card's button adds its
 * Design to the bag as published, in its starting colour, without opening it: the Design's locked choices (which the app
 * keeps on the Design and Liquid puts on the button) are validated by the app like the product page's Add to bag, then
 * the signed line goes to the cart and the bag opens. A Design that changed since the page loaded asks to be opened.
 */
export const QUICK_ADD_STALE = 'This design just changed. Open it to see the latest version.';
const text = value => typeof value === 'string' && value.length > 0;

/** The validation request for a Design's locked choices, or null when the card carries none usable. */
export function quickAddBody(choices, requestId) {
  if (!choices || typeof choices !== 'object' || !text(choices.handle) || !['releaseId', 'releaseDigest', 'productId', 'variantId', 'formatId'].every(key => text(choices[key]))
    || !choices.selection || typeof choices.selection !== 'object' || Array.isArray(choices.selection)) return null;
  const { releaseId, releaseDigest, productId, variantId, formatId, selection } = choices;
  return { schemaVersion: 2, releaseId, releaseDigest, productId, variantId, formatId, requestId, selection, design: choices.handle };
}

/** The bag's picture of the line: the Design's card image (cart.js rebuilds it from an allow-list). */
export function quickAddSnapshot(image) {
  if (/^\/\//.test(image || '')) image = `https:${image}`; // Liquid's image_url is protocol-relative
  if (!/^https:\/\/[^"'<>\s]+$/.test(image || '')) return null;
  return { html: `<div style="width:192px;height:192px"><img src="${image}" style="width:100%;height:100%;object-fit:contain"></div>`, size: 192 };
}

/**
 * Validates and adds one Design. Dependencies come in so the browser passes the theme's cart modules and tests pass
 * their own. Returns the cart result; throws an Error whose message the card shows.
 */
export async function quickAddDesign(card, deps) {
  const { fetcher = fetch, addOne, bagLineId, rememberBagDesign, recordPreorder, validReceiptProperties, root, requestId = crypto.randomUUID() } = deps;
  const body = quickAddBody(card.choices, requestId);
  if (!body) throw Error('Open this design to add it to your bag.');
  let response;
  try { response = await fetcher(`${card.proxy}/configurations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(15000) }); }
  catch { throw Error('Validation was not confirmed. Please try again.'); }
  const accepted = await response.json().catch(() => ({}));
  if (!response.ok) throw Error(response.status === 409 && accepted.error?.code === 'STALE_CONFIG' ? QUICK_ADD_STALE : accepted.error?.message || 'This design could not be added. Open it to try again.');
  const properties = accepted.properties;
  if (accepted.schemaVersion !== 2 || accepted.productId !== body.productId || accepted.variantId !== body.variantId || accepted.releaseId !== body.releaseId
    || accepted.releaseDigest !== body.releaseDigest || accepted.formatId !== body.formatId || accepted.requestId !== requestId || !validReceiptProperties(properties, requestId))
    throw Error('This design could not be confirmed. Open it to add it to your bag.');
  const variantId = body.variantId.split('/').pop();
  const result = await addOne(root, { variantId, properties });
  const snapshot = quickAddSnapshot(card.image);
  if (snapshot && bagLineId(result.line)) rememberBagDesign(bagLineId(result.line), snapshot);
  // A pre-order line: the order notes it, as the product page does (cart-api recordPreorder); best effort.
  if (card.preorder && card.shipsOn) { try { result.cart = await recordPreorder(root, { variantId, shipsOn: card.shipsOn }); } catch { /* The design is in the bag. */ } }
  return result;
}

if (typeof document !== 'undefined' && !globalThis.flDesignQuickAdd) {
  globalThis.flDesignQuickAdd = true;
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-design-quick-add]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const status = button.parentElement?.querySelector('[data-design-quick-add-status]');
    const label = button.textContent;
    button.setAttribute('aria-busy', 'true'); button.textContent = button.dataset.addingLabel || label;
    if (status) status.textContent = '';
    try {
      const [{ addOne, bagLineId, recordPreorder, rememberBagDesign }, { routes }, { validReceiptProperties }] = await Promise.all([
        import('folded-label/cart-api'), import('folded-label/cart'), import('folded-label/personalization-contract')]);
      let choices = null; try { choices = JSON.parse(button.dataset.designChoices || 'null'); } catch { /* refused below */ }
      const proxy = `${routes.root}${(button.dataset.proxyPath || 'apps/folded-label').replace(/^\/+|\/+$/g, '')}`;
      // A fixed Design (owner, 1 Nov 2026) is signed for its published version, in its starting colour.
      const result = button.dataset.designKind === 'fixed' ? await (await import('folded-label/fixed-design')).addFixedDesign({ proxy,
        handle: button.dataset.designHandle, version: button.dataset.designVersion, variantId: button.dataset.variantId, quantity: 1,
        image: button.dataset.designImage, preorder: button.dataset.preorder === 'true', shipsOn: button.dataset.shipsOn },
      { addOne, bagLineId, recordPreorder, rememberBagDesign, root: routes.root }) : await quickAddDesign({ choices, proxy, image: button.dataset.designImage, preorder: button.dataset.preorder === 'true', shipsOn: button.dataset.shipsOn },
        { addOne, bagLineId, recordPreorder, rememberBagDesign, validReceiptProperties, root: routes.root });
      if (result.cart) document.dispatchEvent(new CustomEvent('fl:cart-updated', { detail: result.cart }));
      document.dispatchEvent(new CustomEvent('fl:open-cart', { detail: { opener: button } }));
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally { button.removeAttribute('aria-busy'); button.textContent = label; }
  });
}
